// pages/SlidersPage.jsx
import { useState, useEffect } from "react";
import {
  getAllSliders,
  createSlider,
  updateSlider,
  deleteSlider,
  setShowSlider,
  setHiddenSlider,
  uploadSliderImage,
} from "../../api/sliderApi";
import Loading from "../loading";
import GetAdmin from "../../utils/getAdmin";
import NotFoundPage from "./404";
import Header from "../Header";
import Navigation from "../Navigation";
import Notification from "../Notification";
import {
  PencilSquareIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

const SlidersPage = () => {
  // ---------- state ----------
  const [loading, setLoading] = useState(false);
  const [adminData, setAdminData] = useState(null);
  const [sliders, setSliders] = useState([]);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationProps, setNotificationProps] = useState({});
  const [refresh, setRefresh] = useState(false);

  // state فرم
  const [editingSlider, setEditingSlider] = useState(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [link, setLink] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [order, setOrder] = useState(1000);

  // ---------- دریافت اطلاعات کاربر ----------
  useEffect(() => {
    const fetchAdmin = async () => {
      const admin = await GetAdmin();
      setAdminData(admin);
    };
    fetchAdmin();
  }, []);

  useEffect(() => {
    const fetchSliders = async () => {
      setLoading(true);
      try {
        const res = await getAllSliders();

        let data = [];
        if (res?.data?.sliders) {
          data = res.data.sliders;
        } else if (res?.data?.data) {
          data = res.data.data;
        } else if (Array.isArray(res?.data)) {
          data = res.data;
        } else if (Array.isArray(res)) {
          data = res;
        }

        setSliders(data);
      } catch (err) {
        console.error("خطا در دریافت اسلایدرها:", err);
        showError("خطا در دریافت اسلایدرها");
      } finally {
        setLoading(false);
      }
    };
    if (adminData) {
      fetchSliders();
    }
  }, [adminData, refresh]);

  // ---------- توابع اعلان ----------
  const showSuccess = (msg) => {
    setNotificationProps({ message: msg, type: "success", duration: 3000 });
    setShowNotification(true);
  };

  const showError = (msg) => {
    setNotificationProps({ message: msg, type: "error", duration: 4000 });
    setShowNotification(true);
  };

  // ---------- آپلود تصویر با محدودیت ۵۰۰ کیلوبایت ----------
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const MAX_SIZE = 500 * 1024; // 500 KB

    setUploadError("");

    if (file.size > MAX_SIZE) {
      const fileSizeKB = (file.size / 1024).toFixed(0);
      setUploadError(
        `حجم فایل نباید بیشتر از ۵۰۰ کیلوبایت باشد (حجم فعلی: ${fileSizeKB} KB)`,
      );
      e.target.value = "";
      setTimeout(() => setUploadError(""), 5000);
      return;
    }

    setUploadingImage(true);
    try {
      const res = await uploadSliderImage(file);

      const url =
        res?.data?.url || res?.data?.imageUrl || res?.data?.data?.url || "";
      if (url) {
        setImageUrl(url);
        showSuccess("تصویر با موفقیت آپلود شد");
      } else {
        showError("آدرس تصویر دریافت نشد");
      }
    } catch (err) {
      console.error("خطا در آپلود تصویر:", err);
      showError("خطا در آپلود تصویر");
    } finally {
      setUploadingImage(false);
      e.target.value = "";
    }
  };

  // ---------- ویرایش/افزودن ----------
  const handleEdit = (slider) => {
    setEditingSlider(slider);
    setTitle(slider.title || "");
    setDescription(slider.description || "");
    setLink(slider.link || "");
    setImageUrl(slider.imageUrl || "");
    setIsActive(slider.isActive !== false);
    setOrder(slider.order || 1000);
    setShowForm(true);
    setTimeout(() => {
      document.getElementById("slider-form")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  const handleCancelEdit = () => {
    setEditingSlider(null);
    setTitle("");
    setDescription("");
    setLink("");
    setImageUrl("");
    setIsActive(true);
    setOrder(1000);
    setShowForm(false);
    setUploadError("");
  };

  // ---------- ذخیره اسلایدر ----------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !imageUrl.trim()) {
      showError("لطفاً عنوان و تصویر را وارد کنید.");
      return;
    }

    setSubmitting(true);
    setLoading(true);

    try {
      // ===== افزودن link به payload =====
      const payload = {
        title: title.trim(),
        description: description.trim(),
        link: link.trim(), // ← این خط اضافه شد
        imageUrl: imageUrl.trim(),
        show: isActive,
        order: order,
      };

      if (editingSlider) {
        const res = await updateSlider({
          id: editingSlider._id,
          data: payload,
        });
        console.log(res);
        showSuccess("اسلایدر با موفقیت ویرایش شد.");
      } else {
        const res = await createSlider(payload);
        if (res.status === 201) {
          showSuccess(res.data.message);
        } else {
          showError(res.data.error);
        }
      }

      handleCancelEdit();
      setRefresh((prev) => !prev);
    } catch (err) {
      console.error("خطا در ذخیره اسلایدر:", err);
      showError(err?.response?.data?.message || "خطا در ذخیره اسلایدر");
    } finally {
      setSubmitting(false);
      setLoading(false);
    }
  };

  // ---------- تغییر وضعیت فعال/غیرفعال (در لیست) ----------
  const handleToggleStatus = async (slider) => {
    try {
      if (slider.show) {
        const res = await setHiddenSlider(slider._id);
        if (res.status === 200) {
          showSuccess("اسلایدر غیرفعال شد.");
        }
      } else {
        const res = await setShowSlider(slider._id);
        if (res.status === 200) {
          showSuccess("اسلایدر فعال شد.");
        }
      }
      setRefresh((prev) => !prev);
    } catch (err) {
      console.error("خطا در تغییر وضعیت:", err);
      showError("خطا در تغییر وضعیت اسلایدر");
    }
  };

  // ---------- حذف اسلایدر ----------
  const handleDelete = async (id) => {
    if (!window.confirm("آیا از حذف این اسلایدر مطمئن هستید؟")) return;
    try {
      await deleteSlider(id);
      showSuccess("اسلایدر با موفقیت حذف شد.");
      setRefresh((prev) => !prev);
    } catch (err) {
      console.error("خطا در حذف اسلایدر:", err);
      showError("خطا در حذف اسلایدر");
    }
  };

  // ---------- بررسی دسترسی ----------
  if (!adminData) return <Loading />;
  if (adminData.role !== "manager" && adminData.role !== "postMaker") {
    return <NotFoundPage />;
  }

  // ---------- رندر ----------
  return (
    <div className="w-full flex justify-center flex-col items-center">
      {showNotification && (
        <Notification
          {...notificationProps}
          setShowNotification={setShowNotification}
          showNotification={showNotification}
        />
      )}

      <Header adminName={adminData.name} />

      <div className="flex flex-col md:flex-row gap-6 justify-between w-full px-4">
        <div className="w-full md:w-1/4 mb-6 lg:mb-0">
          <Navigation />
        </div>

        <div className="w-full md:w-3/4">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-800">
                مدیریت اسلایدرها
              </h2>
              <button
                onClick={() => {
                  if (!showForm) {
                    setEditingSlider(null);
                    setTitle("");
                    setDescription("");
                    setLink("");
                    setImageUrl("");
                    setIsActive(true);
                    setOrder(1000);
                    setShowForm(true);
                    setUploadError("");
                  } else {
                    handleCancelEdit();
                  }
                }}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {showForm ? (
                  <>
                    <XMarkIcon className="w-5 h-5" />
                    بستن فرم
                  </>
                ) : (
                  <>
                    <PlusIcon className="w-5 h-5" />
                    افزودن اسلایدر جدید
                  </>
                )}
              </button>
            </div>

            {/* ---------- فرم افزودن/ویرایش ---------- */}
            {showForm && (
              <div
                id="slider-form"
                className="mb-6 border border-gray-200 rounded-lg p-4 bg-gray-50"
              >
                <h3 className="font-semibold text-gray-700 mb-3">
                  {editingSlider ? "ویرایش اسلایدر" : "افزودن اسلایدر جدید"}
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* عنوان */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        عنوان <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                        placeholder="عنوان اسلایدر"
                        required
                      />
                    </div>

                    {/* لینک */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        لینک (اختیاری)
                      </label>
                      <input
                        type="url"
                        value={link}
                        onChange={(e) => setLink(e.target.value)}
                        className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                        placeholder="https://example.com"
                        dir="ltr"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        لینکی که کاربر با کلیک روی اسلایدر به آن هدایت می‌شود.
                      </p>
                    </div>
                  </div>

                  {/* توضیحات */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      توضیحات
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows="2"
                      className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                      placeholder="توضیحات اسلایدر"
                    />
                  </div>

                  {/* وضعیت نمایش */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      وضعیت نمایش
                    </label>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setIsActive(true)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                          isActive
                            ? "bg-green-500 text-white shadow-md"
                            : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                        }`}
                      >
                        <EyeIcon className="w-4 h-4 inline ml-1" />
                        نمایش داده شود
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsActive(false)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                          !isActive
                            ? "bg-red-500 text-white shadow-md"
                            : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                        }`}
                      >
                        <EyeSlashIcon className="w-4 h-4 inline ml-1" />
                        نمایش داده نشود
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {isActive
                        ? "✅ این اسلایدر در صفحه اصلی نمایش داده می‌شود"
                        : "❌ این اسلایدر در صفحه اصلی نمایش داده نمی‌شود"}
                    </p>
                  </div>

                  {/* آپلود تصویر */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      تصویر <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="relative">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="absolute inset-0 opacity-0 cursor-pointer w-full h-full"
                          disabled={uploadingImage}
                        />
                        <button
                          type="button"
                          className="px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg text-sm hover:bg-indigo-200 transition-colors disabled:opacity-50"
                          disabled={uploadingImage}
                        >
                          {uploadingImage ? "در حال آپلود..." : "انتخاب تصویر"}
                        </button>
                      </div>
                      <input
                        type="text"
                        value={imageUrl}
                        onChange={(e) => setImageUrl(e.target.value)}
                        className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 min-w-[200px]"
                        placeholder="آدرس تصویر"
                        required
                        dir="ltr"
                      />
                    </div>
                    {uploadError && (
                      <p className="mt-2 text-sm text-red-600">{uploadError}</p>
                    )}
                    {imageUrl && (
                      <div className="mt-2">
                        <img
                          src={imageUrl}
                          alt="پیش‌نمایش"
                          className="h-16 w-auto object-cover rounded-lg border border-gray-300"
                        />
                      </div>
                    )}
                  </div>

                  {/* شماره اولویت نمایش */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      شماره اولیت نمایش
                    </label>
                    <input
                      type="number"
                      value={order}
                      onChange={(e) => setOrder(Number(e.target.value))}
                      className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500"
                      placeholder="ترتیب نمایش"
                    />
                  </div>

                  {/* دکمه‌ها */}
                  <div className="flex gap-3 pt-2">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                    >
                      {submitting
                        ? "در حال ذخیره..."
                        : editingSlider
                          ? "ویرایش اسلایدر"
                          : "افزودن اسلایدر"}
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                    >
                      انصراف
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* ---------- لیست اسلایدرها ---------- */}
            {loading ? (
              <Loading />
            ) : sliders.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
                  <svg
                    className="w-8 h-8 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <p className="text-gray-500">هیچ اسلایدری یافت نشد</p>
                <p className="text-gray-400 text-sm mt-1">
                  با کلیک روی دکمه «افزودن اسلایدر جدید» اولین اسلایدر را ایجاد
                  کنید.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {sliders.map((slider, index) => {
                  const isActive = slider.isActive !== false;
                  return (
                    <div
                      key={slider._id || index}
                      className={`flex flex-col md:flex-row items-start md:items-center justify-between p-4 border rounded-lg transition-all duration-200 ${
                        isActive
                          ? "bg-white border-gray-200 hover:shadow-md"
                          : "bg-gray-50 border-gray-300 opacity-60"
                      }`}
                    >
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <img
                          src={slider.imageUrl}
                          alt={slider.title}
                          className="w-20 h-20 object-cover rounded-lg flex-shrink-0 border border-gray-200"
                        />
                        <div className="min-w-0 flex-1">
                          <h3 className="font-semibold text-gray-800 text-base truncate">
                            {slider.title}
                          </h3>
                          <p className="text-sm text-gray-500 truncate">
                            {slider.description || "بدون توضیحات"}
                          </p>
                          <div className="flex flex-wrap items-center gap-2 mt-1.5">
                            <span
                              className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${
                                isActive
                                  ? "bg-green-100 text-green-700"
                                  : "bg-gray-200 text-gray-600"
                              }`}
                            >
                              {isActive ? "فعال" : "غیرفعال"}
                            </span>
                            <span className="text-xs text-gray-400">
                              ترتیب: {slider.order || index + 1}
                            </span>
                            {slider.link && (
                              <a
                                href={slider.link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-500 hover:underline truncate max-w-[200px]"
                                dir="ltr"
                              >
                                🔗 {slider.link}
                              </a>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 mt-3 md:mt-0 flex-wrap">
                        <button
                          onClick={() => handleToggleStatus(slider)}
                          className={`flex items-center gap-1 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                            slider.show
                              ? "bg-yellow-100 text-yellow-700 hover:bg-yellow-200"
                              : "bg-green-500 text-white hover:bg-green-200"
                          }`}
                        >
                          {slider.show ? (
                            <>
                              <EyeSlashIcon className="w-4 h-4" />
                              غیرفعال کردن
                            </>
                          ) : (
                            <>
                              <EyeIcon className="w-4 h-4" />
                              فعال کردن
                            </>
                          )}
                        </button>

                        <button
                          onClick={() => handleEdit(slider)}
                          className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                          title="ویرایش"
                        >
                          <PencilSquareIcon className="w-4 h-4" />
                        </button>

                        <button
                          onClick={() => handleDelete(slider._id)}
                          className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                          title="حذف"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SlidersPage;
